import { useCallback, useEffect, useRef, useState } from 'react';
import { IFlow } from 'app/pages/Home';
import { CommissionType } from 'hooks/useCommission';
import { IResponseSalaryCalculationValues } from 'services/http/flow';
import { TOTAL_PERCENTAGE } from 'utils/constants';
import { multiplyPercentageFields } from 'utils/form';
import { unMaskThousands } from 'utils/Numbers';
import {
	accessoriesSaleHelper,
	commissionOnMarginHelper,
	commissionOnSaleHelper,
	financingAwardHelper,
	formattedCommission,
	forwardingAndTccbAwardHelper,
} from 'utils/wageHelpers';

const CAPTURE_AWARD = 200;
const PREOWNED_ACCESSORIES_SALE_PERCENTAGE = 3;

interface UseCalcFieldsParams {
	formValues: IFlow;
	handleCommission: (totalSale: number, type: CommissionType) => number;
	salaryCalculationValues?: IResponseSalaryCalculationValues;
	placementMonthlyData?: Record<string, number>;
	placementYearValue?: string;
	department?: string;
}

export const useCalcFields = ({
	formValues,
	handleCommission,
	salaryCalculationValues,
	placementMonthlyData,
	placementYearValue,
	department,
}: UseCalcFieldsParams) => {
	const [formCalc, setFormCalc] = useState<IFlow>({});

	const prevCalcFinancing = useRef<number>(Number(formValues.financing || 0));
	const financingUserOverride = useRef<boolean>(false);
	const financingUserValue = useRef<number>(0);

	const prevCalcContracts = useRef<number>(Number(formValues.contracts || 0));
	const contractsUserOverride = useRef<boolean>(false);
	const contractsUserValue = useRef<number>(0);

	const prevCalcTccb = useRef<number>(Number(formValues.tccbRevenueTccb || 0));
	const tccbUserOverride = useRef<boolean>(false);
	const tccbUserValue = useRef<number>(0);

	const prevCalcFinancingIncome = useRef<number>(Number(formValues.financingFinancingIncome || 0));
	const financingIncomeUserOverride = useRef<boolean>(false);
	const financingIncomeUserValue = useRef<number>(0);

	const calcFields = useCallback(() => {
		// StoreFlowForm -> CALCULOS

		//CÁLCULO FLUXO LOJA QUANTIDADE ATENDIMENTOS
		const shopFlowCustomerServiceQuantity = Math.round(
			Number(formValues?.passingShopFlow || 0) / Number(formValues?.shopSellers || 1),
		);

		//CÁLCULO LIGAÇÕES QUANTIDADE ATENDIMENTOS
		const callsCustomerServiceQuantity = Math.ceil(
			multiplyPercentageFields(formValues?.incomingCalls, formValues?.attendance),
		);

		//CÁLCULO PROSPECÇÃO QUANTIDADE ATENDIMENTOS
		const prospectionCustomerServiceQuantity = Math.ceil(
			multiplyPercentageFields(formValues?.prospection, formValues?.conversionOnFlow),
		);

		//CÁLCULO LEADS QUANTIDADE ATENDIMENTOS
		const leadsCustomerServiceQuantity = Math.ceil(
			multiplyPercentageFields(formValues?.leads, formValues?.leadsConversionOnFlow),
		);

		//CÁLCULO CLIENTE OFICINA QUANTIDADE ATENDIMENTOS
		const quantity = Math.ceil(
			multiplyPercentageFields(formValues?.customerWorkshop, formValues?.conversionEvaluation),
		);

		// ServiceChannelsTable -> CALCULOS

		//CÁLCULO TOTAL ATENDIMENTOS
		const totalCalls =
			shopFlowCustomerServiceQuantity +
			callsCustomerServiceQuantity +
			prospectionCustomerServiceQuantity +
			leadsCustomerServiceQuantity +
			quantity;

		//CÁLCULO VENDAS DE FLUXO DE LOJA PASSANTES
		const flowSales = Math.floor(
			Number(shopFlowCustomerServiceQuantity || 0) *
				(Number(unMaskThousands(formValues.flowConversion || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO VENDAS DE COMPARECIMENTO LIGAÇÕES
		const callAttendanceSales = Math.floor(
			Number(callsCustomerServiceQuantity || 0) *
				(Number(unMaskThousands(formValues.callAttendanceConversion || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO VENDAS DE PROSPECÇÃO
		const prospectingSales = Math.floor(
			Number(prospectionCustomerServiceQuantity || 0) *
				(Number(unMaskThousands(formValues.prospectingConversion || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO VENDAS DE LEADS
		const leadsSales = Math.floor(
			Number(leadsCustomerServiceQuantity || 0) *
				(Number(unMaskThousands(formValues.leadsConversion || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO VENDAS DE CLIENTE OFICINA
		const workshopCustomerSales = Math.round(
			Number(quantity || 0) * (Number(unMaskThousands(formValues.workshopCustomerConversion || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO TOTAL VENDA
		const totalSales = flowSales + callAttendanceSales + prospectingSales + leadsSales + workshopCustomerSales;

		//CÁLCULO TOTAL CONVERSÃO
		const totalConversion = Math.round((totalSales / totalCalls) * TOTAL_PERCENTAGE);

		// VehicleProfitabilityForm -> CALCULOS

		// Cálculo Margem Bruta por Unidade
		const unitGrossMargin =
			Math.round(
				(Number(unMaskThousands(formValues?.averageTicket || 0)) * Number(unMaskThousands(formValues.margin || 0))) /
					TOTAL_PERCENTAGE,
			) - Number(unMaskThousands(formValues.validGift || 0));

		// Cálculo Margem Total
		const vehicleProfitabilityTotalMargin = Math.round(unMaskThousands(unitGrossMargin || 0)) * Number(totalSales);

		// Cálculo Financiamento
		const newCalcFinancing = Math.floor(
			multiplyPercentageFields(Number(totalSales), formValues?.financingPenetrationAmount),
		);
		const currentFinancing = Number(formValues.financing || 0);
		if (!financingUserOverride.current && currentFinancing !== prevCalcFinancing.current) {
			financingUserOverride.current = true;
			financingUserValue.current = currentFinancing;
		} else if (financingUserOverride.current) {
			financingUserValue.current = currentFinancing;
		}
		const financing = financingUserOverride.current ? financingUserValue.current : newCalcFinancing;
		prevCalcFinancing.current = financing;

		// Cálculo Receita média/Contrato
		const A = Math.round(
			(Number(totalSales) * unMaskThousands(formValues.financingPenetrationAmount || 0)) / TOTAL_PERCENTAGE,
		);
		const B1 = A * Number(unMaskThousands(formValues.averageTicket || 0));
		const B2 = (B1 * Number(unMaskThousands(formValues.financedAmount || 0))) / TOTAL_PERCENTAGE;
		const B3 = (B2 * Number(unMaskThousands(formValues.rApplied || 0))) / TOTAL_PERCENTAGE;

		const averageContractRevenue = B3 / A || 0;

		// Cálculo Contratos
		const newCalcContracts = Math.floor(
			(Number(financing) * unMaskThousands(formValues.tccbPenetrationAmount || 0)) / TOTAL_PERCENTAGE,
		);
		const currentContracts = Number(formValues.contracts || 0);
		if (!contractsUserOverride.current && currentContracts !== prevCalcContracts.current) {
			contractsUserOverride.current = true;
			contractsUserValue.current = currentContracts;
		} else if (contractsUserOverride.current) {
			contractsUserValue.current = currentContracts;
		}
		const contracts = contractsUserOverride.current ? contractsUserValue.current : newCalcContracts;
		prevCalcContracts.current = contracts;

		// Cálculo Receita Financiamento
		const newCalcFinancingIncome = Number(unMaskThousands(averageContractRevenue || 0)) * Number(financing);
		const currentFinancingIncome = Number(formValues.financingFinancingIncome || 0);
		if (!financingIncomeUserOverride.current && currentFinancingIncome !== prevCalcFinancingIncome.current) {
			financingIncomeUserOverride.current = true;
			financingIncomeUserValue.current = currentFinancingIncome;
		} else if (financingIncomeUserOverride.current) {
			financingIncomeUserValue.current = currentFinancingIncome;
		}
		const financingFinancingIncome = financingIncomeUserOverride.current
			? financingIncomeUserValue.current
			: newCalcFinancingIncome;
		prevCalcFinancingIncome.current = financingFinancingIncome;

		// Cálculo Receita T.C.C.B
		const newCalcTccb = unMaskThousands(formValues.averageContractValue || 0) * Number(contracts);
		const currentTccb = Number(formValues.tccbRevenueTccb || 0);
		if (!tccbUserOverride.current && currentTccb !== prevCalcTccb.current) {
			tccbUserOverride.current = true;
			tccbUserValue.current = currentTccb;
		} else if (tccbUserOverride.current) {
			tccbUserValue.current = currentTccb;
		}
		const tccbRevenueTccb = tccbUserOverride.current ? tccbUserValue.current : newCalcTccb;
		prevCalcTccb.current = tccbRevenueTccb;

		// SalesPriceForm -> CALCULOS

		//CÁLCULO LUCRO DOCUMENTO
		const profitDocument =
			unMaskThousands(formValues.averageSaleValue || 0) - unMaskThousands(formValues.averageValueCost || 0);

		// DispatcherForm -> CALCULOS

		//CÁLCULO RECEITA DESPACHANTE
		const dispatcherRevenue = profitDocument * Number(totalSales);

		//CÁLCULO RECEITA MEDIA CARRO
		const averageCarRevenue = Number(totalSales) > 0 ? dispatcherRevenue / Number(totalSales) : 0;

		//CÁLCULO VENDA ACESSORIOS
		const accessoriesSaleAccessories = unMaskThousands(formValues?.unitSales || 0) * Number(totalSales || 0);

		//CÁLCULO QUANTIDADE AVALIACOES
		const reviewsAmount = Math.round(
			Number(totalCalls || 0) * Number(unMaskThousands(formValues?.assessmentRate || 0) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO TOTAL CAPTURA
		const totalCatch = Math.round(
			unMaskThousands(formValues?.fundingRate || 0) * (Number(unMaskThousands(reviewsAmount || 0)) / TOTAL_PERCENTAGE),
		);

		//CÁLCULO PREMIACAO CAPTURA
		const usedPickupCaptureAward = unMaskThousands(formValues?.assessmentRate || 0) > 54 ? CAPTURE_AWARD : 0;

		//CÁLCULO PENETRATION DESPACHANTE
		const [placementYearStr, placementMonthStr] = String(placementYearValue || '').split('-');
		const placementPrevMonthKey =
			Number(placementMonthStr) > 1
				? `${placementYearStr}-${Number(placementMonthStr) - 1}`
				: `${Number(placementYearStr) - 1}-12`;
		const placementAmount =
			placementMonthlyData?.[String(placementYearValue)] ?? placementMonthlyData?.[placementPrevMonthKey];

		const penetrationAmount =
			Number(totalSales) > 0
				? Number((((placementAmount || 0) / Number(totalSales)) * TOTAL_PERCENTAGE).toFixed(2))
				: 0;

		// WageForm -> CALCULOS

		//QUANTIDADE DE VENDAS
		const quantitySale = totalSales;

		// RECEITA FINANCIAMENTO → Receita financiamento (%)
		const financingIncome = financingFinancingIncome;

		// RECEITA DESPACHANTE → Receita despachante (%)
		const salaryDispatcherRevenue = dispatcherRevenue;

		// RECEITA T.C.C.B. → Receita T.C.C.B. (%)
		const revenueTccb = tccbRevenueTccb;

		// Cálculo Valor venda
		const saleValue = Number(formValues?.averageTicket) * Number(totalSales);

		// Cálculo Comissão sobre venda
		const commissionOnSaleAPercentage = commissionOnSaleHelper(Number(totalSales));
		const commissionOnSaleBPercentage =
			handleCommission(totalSales, 'Sale') || Number(salaryCalculationValues?.commissionOnSale) || 0;
		const commissionOnSalePercentage = commissionOnSaleAPercentage + (commissionOnSaleBPercentage || 0);
		const commissionOnSale = unMaskThousands(formattedCommission(commissionOnSalePercentage, saleValue));

		// Cálculo Margem total
		const totalMargin = Number(unitGrossMargin) * Number(quantitySale);

		// Cálculo Comissão sobre margem
		const commissionOnMarginAPercentage = commissionOnMarginHelper(Number(totalSales));
		const commissionOnMarginBPercentage =
			handleCommission(totalSales, 'Margin') || Number(salaryCalculationValues?.comissionOnMargin) || 0;
		const comissionOnMarginPercentage = commissionOnMarginAPercentage + (commissionOnMarginBPercentage || 0);
		const comissionOnMargin = unMaskThousands(formattedCommission(comissionOnMarginPercentage, totalMargin));

		// Cálculo Comissão receita financiamento
		const financingCommissionPercentage =
			handleCommission(totalSales, 'Financing') || salaryCalculationValues?.financingIncome || 0;
		const financingCommission = unMaskThousands(formattedCommission(financingCommissionPercentage, financingIncome));

		// Cálculo Comissão receita despachante
		const forwardingCommissionPercentage =
			handleCommission(totalSales, 'Dispatcher') || salaryCalculationValues?.dispatcherRevenue || 0;
		const forwardingCommission = unMaskThousands(
			formattedCommission(forwardingCommissionPercentage, salaryDispatcherRevenue),
		);

		// Cálculo Comissão receita T.C.C.B
		const commissionOnTCCBPercentage =
			handleCommission(totalSales, 'TCCB') || Number(salaryCalculationValues?.revenueTccb) || 0;
		const tccbCommission = unMaskThousands(formattedCommission(commissionOnTCCBPercentage, revenueTccb));

		// Cálculo Prêmio financiamento
		const financingAwardA = financingAwardHelper(Number(totalSales));
		const fundingAward = (financingAwardA / TOTAL_PERCENTAGE) * Number(financingFinancingIncome);

		// Cálculo Prêmio despachante
		const forwardingAwardA = forwardingAndTccbAwardHelper(Number(totalSales));
		const dispatcherAward = (forwardingAwardA / TOTAL_PERCENTAGE) * Number(dispatcherRevenue);

		// Cálculo Prêmio T.C.C.B
		const tccbAwardA = forwardingAndTccbAwardHelper(Number(totalSales));
		const tccbAward = (tccbAwardA / TOTAL_PERCENTAGE) * Number(tccbRevenueTccb);

		// Cálculo Venda acessório
		// Seminovos: percentual sempre fixo em 3%, sem escalonamento por faixa de venda
		// Demais departamentos: faixa definida pelo valor de "Venda acessórios (R$)" (accessoriesSaleAccessories)
		const companySalesPercentage =
			department === 'preowned'
				? PREOWNED_ACCESSORIES_SALE_PERCENTAGE
				: accessoriesSaleHelper(Number(accessoriesSaleAccessories));
		const salarySaleAccessories = Number(accessoriesSaleAccessories) * (companySalesPercentage / TOTAL_PERCENTAGE);

		// Cálculo Premiação por captura
		const salaryCaptureAward = Number(salaryCalculationValues?.captureAward) * Number(totalCatch);

		// Cálculo DSR
		const dsrPart1 = financingCommission + forwardingCommission + tccbCommission;
		const dsrPart2 = (totalMargin * comissionOnMarginPercentage) / TOTAL_PERCENTAGE;
		const dsrPart3 = saleValue * (commissionOnSalePercentage / TOTAL_PERCENTAGE);
		const sundayHolidaysNum = Number(formValues?.sundayHolidays || 0);
		const dsrPart4 =
			sundayHolidaysNum > 0 ? (Number(formValues?.calendarDays || 22) - sundayHolidaysNum) / sundayHolidaysNum : 1;
		const dsr = (dsrPart1 + dsrPart2 + dsrPart3) / dsrPart4;

		const totalSalary =
			commissionOnSale +
			comissionOnMargin +
			financingCommission +
			forwardingCommission +
			tccbCommission +
			fundingAward +
			salaryCaptureAward +
			dispatcherAward +
			tccbAward +
			salarySaleAccessories +
			dsr;

		setFormCalc({
			...formValues,
			shopFlowCustomerServiceQuantity,
			callsCustomerServiceQuantity,
			prospectionCustomerServiceQuantity,
			leadsCustomerServiceQuantity,
			quantity,
			totalCalls,
			flowSales,
			callAttendanceSales,
			prospectingSales,
			leadsSales,
			workshopCustomerSales,
			totalSales,
			totalConversion,
			unitGrossMargin,
			vehicleProfitabilityTotalMargin,
			financing,
			contracts,
			averageContractRevenue,
			financingFinancingIncome,
			tccbRevenueTccb,
			profitDocument,
			averageCarRevenue,
			dispatcherRevenue,
			penetrationAmount,
			accessoriesSaleAccessories,
			reviewsAmount,
			totalCatch,
			usedPickupCaptureAward,
			quantitySale,
			commissionOnSale,
			comissionOnMargin,
			financingCommission,
			forwardingCommission,
			tccbCommission,
			saleValue,
			totalMargin,
			financingIncome,
			salaryDispatcherRevenue,
			revenueTccb,
			fundingAward,
			dispatcherAward,
			tccbAward,
			salarySaleAccessories,
			salaryCaptureAward,
			dsr,
			totalSalary,
		});
	}, [
		formValues,
		handleCommission,
		salaryCalculationValues?.captureAward,
		salaryCalculationValues?.comissionOnMargin,
		salaryCalculationValues?.commissionOnSale,
		salaryCalculationValues?.dispatcherRevenue,
		salaryCalculationValues?.financingIncome,
		salaryCalculationValues?.revenueTccb,
		department,
		placementMonthlyData,
		placementYearValue,
	]);

	useEffect(() => {
		calcFields();
	}, [calcFields, formValues]);

	return { formCalc };
};
